import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Search, FileText, Loader2, CornerDownLeft } from "lucide-react";

const FIELD_LABEL = { text: "Volltext", title: "Gliederung", file: "Dateiname" };

export default function GlobalSearch({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const tRef = useRef();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    clearTimeout(tRef.current);
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    tRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q } });
        setResults(r.data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(tRef.current);
  }, [q, open]);

  const pick = (res, hit) => {
    setOpen(false);
    setQ("");
    setResults([]);
    onSelect(res, hit);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger"
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-300 bg-gray-50 text-sm text-gray-500 hover:bg-gray-100 transition-colors w-full max-w-md"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left truncate">Volltextsuche in allen Heften…</span>
        <kbd className="text-[10px] font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5 shrink-0">⌘K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-2xl gap-0" data-testid="global-search-dialog">
          <DialogTitle className="sr-only">Volltextsuche</DialogTitle>
          <DialogDescription className="sr-only">Suche in Volltext, Gliederung und Dateinamen aller Hefte.</DialogDescription>
          <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-gray-500">
            <CommandInput
              value={q}
              onValueChange={setQ}
              placeholder="Suche in Volltext, Gliederung & Dateinamen…"
              data-testid="global-search-input"
            />
            <CommandList className="max-h-[440px]">
              {loading && (
                <div className="py-8 flex justify-center text-gray-400" data-testid="search-loading">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
              {!loading && q.trim().length >= 2 && results.length === 0 && (
                <CommandEmpty data-testid="search-empty">Keine Treffer für „{q}".</CommandEmpty>
              )}
              {!loading && q.trim().length < 2 && (
                <div className="py-8 text-center text-sm text-gray-400">Mindestens 2 Zeichen eingeben…</div>
              )}
              {!loading && results.map((res) => {
                const hits = res.hits.length ? res.hits : [{ page: 0, snippet: "Treffer im Dateinamen", field: "file" }];
                return (
                  <CommandGroup key={res.doc_id} heading={`${res.filename} · ${(res.land || "").toUpperCase()}`}>
                    {hits.map((hit, i) => (
                      <CommandItem
                        key={i}
                        value={`${res.doc_id}-${i}`}
                        onSelect={() => pick(res, hit)}
                        data-testid={`search-result-${res.doc_id}-${i}`}
                        className="gap-3"
                      >
                        <FileText className="h-4 w-4 text-[#0F4C81] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-wide text-gray-400">
                            Seite {(hit.page || 0) + 1} · {FIELD_LABEL[hit.field] || "Treffer"}
                          </div>
                          <div className="text-sm text-gray-700 truncate">{hit.snippet}</div>
                        </div>
                        <CornerDownLeft className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
